import { getQuestions } from 'common/api/users/questions';
import { useApi } from 'common/modules/async-actions/useApi';
import {
    SecurityQuestions,
    SecurityQuestionsProps,
} from 'features/coordinator/enrollment/steps/SecurityQuestions';
import { Form, Formik, FormikState, useFormikContext } from 'formik';
import React, { useCallback, useContext, useEffect } from 'react';
import { createStyles } from 'utils/createStyle';
import { Document } from '@contentful/rich-text-types';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { BaseStep } from '../BaseStep';
import { FamilyEnrollmentContext } from '../../context';
import { register } from 'common/api/users/service';
import { getRegistrationValidationMessage } from 'common/api/users/getRegistrationValidationMessage';
import { getOrganizationByEnrollmentCode } from 'common/api/crm/organization/enrollment';
import { useBlackBoxValues } from 'modules/fraud-force/fraudForce';
import { APIResponse } from 'common/api/models';
import { SecurityQuestion } from 'common/api/users/models/SecurityQuestion';
import { useAlert } from 'modules/alerts';
import { SERVICE_UNAVAILABLE_MESSAGE } from 'config';
import { ButtonControls } from '../utils';
import { closeLoader, LoadingOverlay, openLoader } from 'components/loading-overlay/LoadingOverlay';
import { sendToLoginPage } from 'features/auth/components/withAuth';
import { Routes } from 'routes';
import { EnrollSourceApplicationIds } from 'common/api/auth/models/RegistrationRequestWithoutOrg';
import { GA_WEB_ENROLLMENT_EXPERIMENT_NAME, logParticipantEnrollment } from 'config/analytics';
import { IS_FLIPGIVE } from 'common/env';

const styles = createStyles({
    formContainer: ['flex flex-col m-6 mt-8'],
    buttonContainer: ['flex flex-row justify-center mt-14'],
});

const initialFormValues = { answer1: '', answer2: '', question1: '', question2: '' };

const getExistingSocialAccountMessage = (provider?: 'google' | 'apple') => {
    if (provider === 'google') {
        return 'Your Google account or email is already associated with an account. Please go back and use a different sign-in method.';
    }

    if (provider === 'apple') {
        return 'Your Apple account or email is already associated with an account. Please go back and use a different sign-in method.';
    }

    return 'This social account or email is already associated with an account. Please go back and use a different sign-in method.';
};

export const useSecurityQuestionsStep = () => {
    const [loadQuestions, { loading }, questions] = useApi(getQuestions);
    const {
        stepHeaders,
        userAccountInfo,
        updateUserAccountInfo,
        setOrganizationTo,
        loader,
        isEnrollmentTestVariant,
    } = useContext(FamilyEnrollmentContext);
    const { retrieveBlackBox } = useBlackBoxValues();
    const alert = useAlert();

    useEffect(() => {
        if (!questions && !loading) {
            loadQuestions();
        }
    }, [loadQuestions, loading, questions]);

    const onSubmit = useCallback(
        async (formValue: typeof initialFormValues) => {
            const blackboxValues = await retrieveBlackBox();
            openLoader(loader?.current as LoadingOverlay);

            const enrollmentCode = localStorage.getItem('RR_enrollmentCode') || '';
            const isSocial = userAccountInfo.registrationMethod === 'social';

            let organizationId = userAccountInfo.organizationId;
            if (!organizationId && enrollmentCode) {
                const orgResponse = await getOrganizationByEnrollmentCode(enrollmentCode);
                if (orgResponse.error || !orgResponse.data?.id) {
                    alert({ message: SERVICE_UNAVAILABLE_MESSAGE });
                    closeLoader(loader?.current as LoadingOverlay);
                    return;
                }
                organizationId = orgResponse.data.id;
                setOrganizationTo(orgResponse.data);
                updateUserAccountInfo((prev) => ({ ...prev, organizationId }));
            }

            const response = await register({
                ...userAccountInfo,
                organizationId,
                enrollmentCode,
                blackBoxValue: blackboxValues.blackBoxValue || '',
                createOneTimeCode: true,
                securityQuestionAnswers: {
                    [formValue.question1]: formValue.answer1,
                    [formValue.question2]: formValue.answer2,
                },
                enrollSourceApplicationId: EnrollSourceApplicationIds.V3Web,
                countryCode: IS_FLIPGIVE ? 'CA' : 'US',
                ...(isSocial ? { password: '', confirmPassword: '' } : {}),
            });

            if (response.error) {
                if (response.error.body?.result === 'InvalidCountryCode') {
                    alert({
                        message: 'Cannot join Organization with different country of origin.',
                    });
                } else if (response.error.body?.result === 'ExistingAccount') {
                    alert({
                        message: isSocial
                            ? getExistingSocialAccountMessage(userAccountInfo.provider)
                            : 'An account with this email already exists.',
                    });
                } else if (
                    response.error.body?.result === 'InvalidExternalRegistrationKey' ||
                    response.error.body?.result === 'ExpiredExternalRegistrationKey' ||
                    response.error.body?.result === 'UsedExternalRegistrationKey'
                ) {
                    alert({
                        message:
                            'Your sign-in session is no longer valid. Please restart registration.',
                    });
                } else {
                    alert({
                        message:
                            getRegistrationValidationMessage(response) ??
                            SERVICE_UNAVAILABLE_MESSAGE,
                    });
                }
                closeLoader(loader?.current as LoadingOverlay);
                return;
            }

            localStorage.removeItem('RR_enrollmentCode');

            logParticipantEnrollment(
                isEnrollmentTestVariant ? 5 : 4,
                'account_security',
                { flowVariant: isEnrollmentTestVariant ? 'variant' : 'control' },
                {
                    experiment_name: GA_WEB_ENROLLMENT_EXPERIMENT_NAME,
                    experiment_variant: isEnrollmentTestVariant ? 'variant' : 'control',
                }
            );

            sendToLoginPage(
                Routes.EnrollFamilySetupRecoveryPhone,
                `${response.data.oneTimeCode ?? ''}`
            );
        },
        [
            retrieveBlackBox,
            userAccountInfo,
            updateUserAccountInfo,
            setOrganizationTo,
            alert,
            loader,
            isEnrollmentTestVariant,
        ]
    );

    return {
        onSubmit,
        questions,
        loading,
        stepHeaders,
    };
};

export const SecurityQuestionStep = () => {
    const { onSubmit, questions, loading, stepHeaders } = useSecurityQuestionsStep();

    if (loading) {
        return (
            <div className="mt-12 justify-center flex">
                <ActivityIndicator />
            </div>
        );
    }

    return (
        <BaseStep
            contentClassName="text-md sm:text-lg font-thin"
            heading={stepHeaders?.securityQuestionsHeading || ''}
            subheading={stepHeaders?.securityQuestionsBody as Document}
        >
            <Formik initialValues={initialFormValues} onSubmit={onSubmit}>
                <Form>
                    <SecurityQuestionForm questions={questions} />
                </Form>
            </Formik>
        </BaseStep>
    );
};

const SecurityQuestionForm = ({
    questions,
}: {
    questions: APIResponse<SecurityQuestion[], null> | undefined;
}) => {
    const { values } = useFormikContext<FormikState<SecurityQuestionsProps['selections']>>();

    return (
        <div className={styles.formContainer}>
            <SecurityQuestions
                questions={questions?.data || null}
                selections={values as unknown as SecurityQuestionsProps['selections']}
            />
            <ButtonControls />
        </div>
    );
};
